/**
 * Artifact-image node — the ProseMirror equivalent of a Word "yaqat-cite-tag"
 * content control wrapping a title/caption + inline picture + source line
 * (see WordDocumentHost.ts's `fillImageControl`, and service/writer/cite.ts's
 * `insertArtifactImage`/`insertMiningFigure` on the yaqatw side).
 *
 * An atom block node, modeled on imageNode.ts's existing NodeView/asset
 * pattern (lazy `loadAssetDataUrl` resolution so getEditorSchema — used
 * headlessly by tests/convert.ts — doesn't drag in fs/stores). Only the
 * image itself is asset-backed (`assetId`); caption/source text are plain
 * node attrs rendered by the NodeView, since (like imageBlock/imageInline)
 * this atom has no editable inner PM content.
 *
 * `captionOoxml` from the wire (real Word OOXML) is deliberately NOT stored
 * or rendered here — `captionLabel`/`captionTitle` are the additive,
 * non-OOXML fields added to the wire specifically so a browser host doesn't
 * need to parse OOXML (see yaqatw's interfaces.ts CitationRefreshPayload).
 * No live auto-number is computed (Word's caption number is a live SEQ
 * field) — an explicit, small v1 gap: the caption renders as
 * "`${captionLabel}: ${captionTitle}`" with no number.
 */

import { Node, mergeAttributes, type NodeViewRenderer } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";

let assetsModulePromise: Promise<typeof import("./assets")> | null = null;
function loadAssetDataUrl(assetId: string): Promise<string | null> {
  assetsModulePromise = assetsModulePromise ?? import("./assets");
  return assetsModulePromise.then((m) => m.loadAssetDataUrl(assetId));
}

const ALIGN_ATTR = {
  default: "center" as "left" | "center" | "right",
  renderHTML: () => ({}),
};

const artifactImageAttributes = {
  controlId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-control-id"),
    renderHTML: (attrs: Record<string, any>) => (attrs.controlId ? { "data-control-id": attrs.controlId } : {}),
  },
  title: { default: null as string | null, renderHTML: () => ({}) },
  source: { default: "", renderHTML: () => ({}) },
  captionLabel: { default: "", renderHTML: () => ({}) },
  captionTitle: { default: "", renderHTML: () => ({}) },
  captionPosition: { default: "below" as "above" | "below", renderHTML: () => ({}) },
  imageAlignment: ALIGN_ATTR,
  sourceAlignment: { default: "left" as "left" | "center" | "right", renderHTML: () => ({}) },
  assetId: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute("data-asset-id"),
    renderHTML: (attrs: Record<string, any>) => (attrs.assetId ? { "data-asset-id": attrs.assetId } : {}),
  },
  naturalWidth: { default: null as number | null, renderHTML: () => ({}) },
  naturalHeight: { default: null as number | null, renderHTML: () => ({}) },
};

function captionText(n: PMNode): string {
  const label = (n.attrs.captionLabel as string) || "";
  const title = (n.attrs.captionTitle as string) || "";
  if (!label) return title;
  if (!title) return label;
  return `${label}: ${title}`;
}

// `getPos` isn't used today (the node has no in-place editable/resizable
// affordance yet); refresh/delete/unlink go through controlRegistry.ts instead.
function artifactImageNodeView(): NodeViewRenderer {
  return ({ node, editor }) => {
    const wrapper = document.createElement("figure");
    wrapper.className = "qda-artifact-image";

    const img = document.createElement("img");
    img.style.display = "block";
    img.style.maxWidth = "100%";
    img.draggable = false;

    const caption = document.createElement("figcaption");
    caption.className = "qda-artifact-caption";
    const source = document.createElement("p");
    source.className = "qda-artifact-source";

    let loadedAssetId: string | null = null;

    const render = (n: PMNode) => {
      wrapper.title = (n.attrs.title as string) || "";
      img.alt = (n.attrs.title as string) || "";
      img.style.textAlign = "";
      img.style.display = "block";
      img.style.margin =
        n.attrs.imageAlignment === "left" ? "0 auto 0 0" : n.attrs.imageAlignment === "right" ? "0 0 0 auto" : "0 auto";

      caption.textContent = captionText(n);
      caption.style.display = caption.textContent ? "" : "none";

      const sourceText = (n.attrs.source as string) || "";
      source.textContent = sourceText;
      source.style.display = sourceText ? "" : "none";
      source.style.textAlign = (n.attrs.sourceAlignment as string) || "left";

      wrapper.innerHTML = "";
      const pieces = n.attrs.captionPosition === "above" ? [caption, img, source] : [img, caption, source];
      for (const piece of pieces) wrapper.appendChild(piece);

      if (n.attrs.assetId !== loadedAssetId) {
        loadedAssetId = n.attrs.assetId;
        const id = loadedAssetId;
        img.removeAttribute("src");
        if (id) {
          loadAssetDataUrl(id).then((url) => {
            if (loadedAssetId !== id) return;
            if (url) img.src = url;
            else img.alt = `[missing image${n.attrs.title ? `: ${n.attrs.title}` : ""}]`;
          });
        }
      }
    };

    render(node);

    return {
      dom: wrapper,
      update: (updated: PMNode) => {
        if (updated.type.name !== node.type.name) return false;
        render(updated);
        return true;
      },
      selectNode: () => {
        if (editor.isEditable) wrapper.classList.add("qda-artifact-image-selected");
      },
      deselectNode: () => wrapper.classList.remove("qda-artifact-image-selected"),
      ignoreMutation: () => true,
      stopEvent: () => false,
    };
  };
}

export const ArtifactImage = Node.create({
  name: "artifactImage",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes: () => artifactImageAttributes,
  parseHTML: () => [{ tag: "figure[data-yaqat-artifact-image]" }],
  renderHTML: ({ HTMLAttributes }) => [
    "figure",
    mergeAttributes(HTMLAttributes, { "data-yaqat-artifact-image": "" }),
  ],
  addNodeView: artifactImageNodeView,
});
